import { AsyncSingleSelectField, CharField, NumberField, SwitchField } from "@inmagik/magik-ui"
import { getIn, useFormikContext } from "formik"
import { Col } from "react-bootstrap"
import type { Path } from "../../types"
import { WysiwygField } from "../../components/WysiwygField"
import { useCreateNamedEntity, useNamedEntitiesLazy, useNamedEntityLazy } from "../../hooks/named-entities"
import NamedEntityModal from "../NamedEntities/NamedEntityModal"
import useModalTrigger from "magik-react-hooks/useModalTrigger"
import { transformErrorForForm } from "../../utils/utils"
import { useCreateTheme, useThemeLazy, useThemesLazy } from "../../hooks/themes"
import ThemeModal from "../Themes/ThemeModal"

export default function PathForm({ col = 8 }: { col?: number }) {
  const { setFieldValue, values, errors, submitCount } = useFormikContext<Path>()

  const fetchNamedEntitiesAuthors = useNamedEntitiesLazy({ entity_type: "AUTHOR" })
  const fetchNamedEntity = useNamedEntityLazy()
  const fetchThemes = useThemesLazy()
  const fetchTheme = useThemeLazy()
  const createNamedEntity = useCreateNamedEntity()
  const createTheme = useCreateTheme()

  const [modalStateCreateNamedEntity, modalActionsCreateNamedEntity] = useModalTrigger()
  const [modalStateCreateTheme, modalActionsCreateTheme] = useModalTrigger()

  return (
    <Col xs={col} className="d-flex flex-column align-items-stretch gap-4">
      <CharField
        label="Titolo"
        layout="horizontal"
        value={values.title}
        onChange={(value) => {
          setFieldValue("title", value)
        }}
        isValid={submitCount > 0 ? !getIn(errors, "title") : undefined}
        errorMessage={getIn(errors, "title")}
        required
      />

      <AsyncSingleSelectField
        label="Tema"
        layout="horizontal"
        placeholder="Seleziona un tema"
        onChange={(value) => {
          setFieldValue("theme", value)
        }}
        required
        clearable
        value={values.theme || null}
        loadInitialValue={(id: number) => fetchTheme(id).then((theme) => ({ label: theme.name, value: theme.id }))}
        isValid={submitCount > 0 ? !getIn(errors, "theme") : undefined}
        errorMessage={getIn(errors, "theme")}
        loadOptions={(inputValue: string) => {
          return fetchThemes({ search: inputValue }).then((themes) => {
            return themes.results?.map((theme) => ({
              label: theme.name,
              value: theme.id,
            }))
          })
        }}
      >
        <AsyncSingleSelectField.Add
          onClick={() => {
            modalActionsCreateTheme.open()
          }}
        />
      </AsyncSingleSelectField>

      <AsyncSingleSelectField
        label="Autore"
        layout="horizontal"
        placeholder="Seleziona un autore"
        onChange={(value) => {
          setFieldValue("author", value)
        }}
        clearable
        value={values.author || null}
        loadInitialValue={(id: number) =>
          fetchNamedEntity(id).then((author) => ({ label: author.name, value: author.id }))
        }
        isValid={submitCount > 0 ? !getIn(errors, "author") : undefined}
        errorMessage={getIn(errors, "author")}
        loadOptions={(inputValue: string) => {
          return fetchNamedEntitiesAuthors({ search: inputValue }).then((authors) => {
            return authors.results?.map((author) => ({
              label: author.name,
              value: author.id,
            }))
          })
        }}
      >
        <AsyncSingleSelectField.Add
          onClick={() => {
            modalActionsCreateNamedEntity.open("AUTHOR")
          }}
        />
      </AsyncSingleSelectField>

      <WysiwygField
        label="Descrizione"
        layout="horizontal"
        value={values.description}
        onChange={(value) => {
          setFieldValue("description", value)
        }}
        isValid={submitCount > 0 ? !getIn(errors, "description") : undefined}
        errorMessage={getIn(errors, "description")}
      />

      <NumberField
        label="Tempo di lettura (minuti)"
        layout="horizontal"
        value={values.time_of_reading}
        onChange={(value) => {
          setFieldValue("time_of_reading", value)
        }}
      />

      <NumberField
        label="Ordine"
        layout="horizontal"
        value={values.order}
        onChange={(value) => {
          setFieldValue("order", value)
        }}
      />

      <SwitchField
        label="Bozza"
        layout="horizontal"
        value={values.draft}
        onChange={(checked) => {
          setFieldValue("draft", checked)
        }}
      />

      {modalStateCreateNamedEntity.isOpen && (
        <NamedEntityModal
          show={modalStateCreateNamedEntity.isOpen}
          onClose={modalActionsCreateNamedEntity.close}
          save={(valuesEntity, actions) => {
            return createNamedEntity
              .mutateAsync(valuesEntity)
              .then((namedEntity) => {
                setFieldValue("author", namedEntity.id)
                modalActionsCreateNamedEntity.close()
              })
              .catch((e) => {
                actions.setErrors(transformErrorForForm(e))
              })
          }}
          title="Nuova entità"
        />
      )}
      {modalStateCreateTheme.isOpen && (
        <ThemeModal
          show={modalStateCreateTheme.isOpen}
          onClose={modalActionsCreateTheme.close}
          save={(valuesEntity, actions) => {
            return createTheme
              .mutateAsync(valuesEntity)
              .then((theme) => {
                setFieldValue("theme", theme.id)
                modalActionsCreateTheme.close()
              })
              .catch((e) => {
                actions.setErrors(transformErrorForForm(e))
              })
          }}
          title="Nuovo tema"
        />
      )}
    </Col>
  )
}
